import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { UtilityService } from '@app/core/services/utility.service';
import { INotification, INotificationSettings, NotificationsService } from '@app/core/services/notifications.service';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';

type NotificationView = '0' | '1' | '2';
type NotificationItem = INotification & { item_is_selected?: '1' | '0' };

@Component({
    selector: 'tt-p2-notifications',
    templateUrl: './p2-notifications.component.html',
    styleUrls: ['./p2-notifications.component.css'],
    standalone: false,
})
export class P2NotificationsComponent implements OnInit {
    public selectedView: NotificationView = '0';
    public setting?: INotificationSettings;
    public unreadNotifications: NotificationItem[] = [];
    public readNotifications: INotification[] = [];
    public locked = false;

    public readonly views = [
        { id: '0' as NotificationView, label: 'notifications_new' },
        { id: '1' as NotificationView, label: 'notifications_read' },
        { id: '2' as NotificationView, label: 'notifications_mark' },
    ];

    public readonly searchButtons: FormButton[] = [{
        id: 'erase', icon: 'fas fa-eraser', type: 'danger', onClick: () => void this.clearSearchText(),
    }];

    private navigator?: TTViewNavigator;

    constructor(
        private notifications: NotificationsService,
        private remember: RememberService,
        private utility: UtilityService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public selectView(view: string): void {
        if (this.views.some((item) => item.id === view)) this.selectedView = view as NotificationView;
    }

    public async readNotification(item: INotification, event?: MouseEvent): Promise<void> {
        await this.notifications.setAsRead(item);
        if (!item.item_state) return;

        const target = { to: item.item_state, parms: item.item_parms };
        if (event && this.navigator && this.utility.isModifierKeyPressed(event)) {
            await this.navigator.newTab(target);
        } else {
            await this.navigator?.go(target);
        }
    }

    public markNotification(item?: NotificationItem): void {
        if (item) {
            item.item_is_selected = item.item_is_selected === '1' ? '0' : '1';
            return;
        }

        for (const notification of this.unreadNotifications) notification.item_is_selected = '1';
    }

    public async confirmMarkNotifications(): Promise<void> {
        if (this.locked) return;
        this.locked = true;
        try {
            const selected = this.unreadNotifications.filter((item) => item.item_is_selected === '1');
            await Promise.all(selected.map((item) => this.notifications.setAsRead(item)));
            await this.reloadNotifications();
        } finally {
            this.locked = false;
        }
    }

    public async onSearchTextChanged(value: string): Promise<void> {
        if (value !== '' || !this.setting) return;
        await this.remember.remember('w_p2notifications.searchtext', '');
    }

    public async onSearchBlur(): Promise<void> {
        const searchtext = this.setting?.searchtext?.trim();
        if (searchtext) await this.remember.remember('w_p2notifications.searchtext', searchtext);
    }

    public async clearSearchText(): Promise<void> {
        if (!this.setting) return;
        this.setting.searchtext = '';
        await this.onSearchTextChanged('');
    }

    public async ngOnInit(): Promise<void> {
        this.setting = await this.notifications.getSettings();
        this.setting.searchtext ??= '';
        await this.reloadNotifications();
    }

    private async reloadNotifications(): Promise<void> {
        const [unread, read] = await Promise.all([
            this.notifications.loadUnreadNotifications(),
            this.notifications.loadReadNotifications(),
        ]);
        this.unreadNotifications = unread.records;
        this.readNotifications = read.records;
    }
}
